import React from 'react'
import { Button, Space, Tooltip } from 'antd'
import { ArrowLeft, Briefcase, Building2, User } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Link, useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { contextCompanySelector } from '../../redux/context/company/selectors'
import { Route } from '../../routes/Route'
import { firstPagePath } from './routeUtils'
import LucideIcon from '../../shared/components/LucideIcon'

const CONTEXT_ORDER = ['/settings/company', '/settings/accountant', '/settings/financier', '/settings/user']

const CONTEXT_ICONS: Record<string, React.ReactNode> = {
  '/settings/company': <LucideIcon icon={Building2} />,
  '/settings/accountant': <LucideIcon icon={Briefcase} />,
  '/settings/financier': <LucideIcon icon={Briefcase} />,
  '/settings/user': <LucideIcon icon={User} />
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 8px;
`

const BackLink = styled(Link)<{ $collapsed?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: ${({ $collapsed }) => ($collapsed ? 'center' : 'flex-start')};
  gap: 8px;
  padding: 4px 8px;
  font-weight: 600;
  color: inherit;
`

const ContextLabel = styled.span`
  display: flex;
  flex-direction: column;
  min-width: 0;

  small {
    overflow: hidden;
    font-weight: normal;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
`

interface SettingsNavHeaderProps {
  /** Children of the /settings route, already filtered by the user's authority. */
  contexts: Route[]
  activeContextPath?: string
  returnPath: string
  collapsed?: boolean
  onNavigate?: () => void
}

const SettingsNavHeader: React.FC<SettingsNavHeaderProps> = ({
  contexts,
  activeContextPath,
  returnPath,
  collapsed,
  onNavigate
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const company = useSelector(contextCompanySelector)

  const visibleContexts = contexts
    .filter(context => !context.hideInMenu && CONTEXT_ORDER.includes(context.path))
    .sort((a, b) => CONTEXT_ORDER.indexOf(a.path) - CONTEXT_ORDER.indexOf(b.path))

  const backLabel = t('global:back-to-app', 'Takaisin sovellukseen')

  return (
    <Wrapper>
      <Tooltip title={collapsed ? backLabel : undefined} placement="right">
        <BackLink to={returnPath} $collapsed={collapsed} onClick={onNavigate}>
          <LucideIcon icon={ArrowLeft} size={16} />
          {!collapsed && backLabel}
        </BackLink>
      </Tooltip>
      <Space.Compact orientation="vertical" block>
        {visibleContexts.map(context => {
          const label = t(`menu:${context.path}`, context.name as string)
          const active = context.path === activeContextPath
          return (
            <Tooltip key={context.path} title={collapsed ? label : undefined} placement="right">
              <Button
                block
                color={active ? 'primary' : 'default'}
                variant={active ? 'filled' : 'outlined'}
                icon={CONTEXT_ICONS[context.path]}
                aria-current={active ? 'page' : undefined}
                style={{
                  height: 'auto',
                  paddingBlock: 6,
                  justifyContent: collapsed ? 'center' : 'flex-start',
                  alignItems: 'flex-start',
                  textAlign: 'start',
                  fontWeight: active ? 600 : undefined
                }}
                onClick={() => {
                  navigate(firstPagePath(context))
                  onNavigate?.()
                }}
              >
                {!collapsed && (
                  <ContextLabel>
                    {label}
                    {active && context.path === '/settings/company' && company?.name && <small>{company.name}</small>}
                  </ContextLabel>
                )}
              </Button>
            </Tooltip>
          )
        })}
      </Space.Compact>
    </Wrapper>
  )
}

export default SettingsNavHeader
